import React from 'react';

/**
 * Rich-text helpers for chat messages.
 *
 * Formatting is encoded as lightweight markdown-style markers inside the plain
 * message string (so the wire format and DB schema stay unchanged):
 *
 *   **bold**        ->  <strong>
 *   *italic*        ->  <em>
 *   _italic_        ->  <em>
 *   ~underline~     ->  underlined <span>
 *   [#ff0000|text]  ->  colored <span> (3- or 6-digit hex only)
 *   https://...     ->  clickable <a> (auto-linked)
 *
 * `renderRichText` returns an array of React nodes — it NEVER produces an HTML
 * string and NEVER uses dangerouslySetInnerHTML, so output is XSS-safe by
 * construction. Only http/https URLs are ever turned into links.
 */

// Matches http(s) URLs. Kept deliberately simple; trailing punctuation that is
// unlikely to be part of a URL is trimmed off after matching (see splitLinks).
const URL_REGEX = /(https?:\/\/[^\s<]+)/gi;

// Matches a mention token `@[displayName](userId)`. The composer writes this
// markup when a suggestion is accepted (see ChatPanel); it carries the stable
// user id so rendering and the "was I mentioned?" check never depend on
// name-matching. displayName forbids `]` and userId forbids `)` so the token
// can't swallow following text. Passes through the backend sanitizer untouched
// (brackets/parens are plain punctuation, same as the [#hex|text] color markup).
const MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

// Inline formatting rules, applied in this order. Each rule's regex captures the
// formatted inner text in `innerGroup` (default 1). renderInline recurses into
// that inner text, so markers NEST: e.g. `**bold *italic* still bold**` works.
//
// Two things make nesting correct:
//  1. The bold/underline inner patterns use a lazy `.+?` (any char, including
//     other markers) rather than "anything but my own marker". That lets a bold
//     span hold an italic span, and the recursion then formats the inner italic.
//  2. Bold (`**`) is listed before italic (`*`); on an equal start index the
//     first rule wins (see renderInline's strict `<`), so `**x**` is taken as
//     bold, never as italic wrapping `*x`. The italic inner is `(?:[^*]|\*\*)+?`
//     — any run of non-`*` chars and/or whole `**` pairs, but never a LONE `*`.
//     That lets `*…**bold**…*` nest a bold inside, while stopping an italic from
//     swallowing the `*` that opens the next neighbour (so `*a* and *b*` stays
//     two spans). No lookbehind — Safari < 16.4 can't parse it, which would
//     break the whole bundle.
//
// The color rule still forbids brackets inside (`[^[\]]`) so nested color spans
// stay unambiguous; its hex group only matches a strict 3/6-digit value, so an
// invalid color (e.g. `[#zzz|x]`) never matches and is left as literal text.
const INLINE_RULES = [
  { name: 'bold', regex: /\*\*([\s\S]+?)\*\*/, render: (inner, key) => <strong key={key}>{inner}</strong> },
  { name: 'italic-star', regex: /\*((?:[^*]|\*\*)+?)\*/, render: (inner, key) => <em key={key}>{inner}</em> },
  { name: 'italic-underscore', regex: /_((?:[^_]|__)+?)_/, render: (inner, key) => <em key={key}>{inner}</em> },
  { name: 'underline', regex: /~([\s\S]+?)~/, render: (inner, key) => <span key={key} style={{ textDecoration: 'underline' }}>{inner}</span> },
  {
    name: 'color',
    regex: /\[(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}))\|([^[\]]+?)\]/,
    innerGroup: 2,
    render: (inner, key, match) => <span key={key} style={{ color: match[1] }}>{inner}</span>,
  },
];

// Trailing characters that are commonly punctuation rather than part of a URL.
const TRAILING_PUNCT = /[.,!?;:)\]}'"]+$/;

/**
 * Apply inline formatting rules to a plain string, recursively, returning an
 * array of React nodes. `keyPrefix` keeps keys unique across the tree.
 */
function renderInline(text, keyPrefix) {
  // Find the earliest match among all inline rules.
  let earliest = null;
  for (const rule of INLINE_RULES) {
    const match = rule.regex.exec(text);
    if (match && (earliest === null || match.index < earliest.match.index)) {
      earliest = { rule, match };
    }
  }

  if (!earliest) {
    return text ? [text] : [];
  }

  const { rule, match } = earliest;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + match[0].length);
  const key = `${keyPrefix}-${rule.name}-${match.index}`;

  const nodes = [];
  if (before) nodes.push(before);
  // Recurse into the matched inner text so formatting can nest (e.g. bold link).
  const inner = renderInline(match[rule.innerGroup || 1], `${key}-in`);
  nodes.push(rule.render(inner, key, match));
  // Recurse into the remainder so multiple markers on one line all render.
  nodes.push(...renderInline(after, `${key}-rest`));
  return nodes;
}

/**
 * Split a string into text segments and mention nodes, extracting
 * `@[name](id)` tokens first so the surrounding text (and any markup inside the
 * display name) is never reinterpreted. Non-mention segments are handed to
 * splitLinks. A mention renders as a styled, non-interactive <span>; when its
 * id matches `opts.currentUserId` it gets the stronger "self" variant so you
 * can spot at a glance that you were the one pinged.
 *
 * @param {string} text
 * @param {string} keyPrefix
 * @param {{ currentUserId?: string, mentionStyle?: object, selfMentionStyle?: object, mentionColors?: Record<string,string> }} opts
 */
function splitMentions(text, keyPrefix, opts) {
  const currentUserId = opts && opts.currentUserId != null ? String(opts.currentUserId) : null;
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;
  MENTION_REGEX.lastIndex = 0;
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const plain = text.slice(lastIndex, match.index);
    if (plain) nodes.push(...splitLinks(plain, `${keyPrefix}-m${i}`));

    const name = match[1];
    const userId = match[2];
    const isSelf = currentUserId !== null && String(userId) === currentUserId;
    // Theme-aware styling is supplied by the caller (richText stays theme-
    // agnostic); fall back to a sensible accent pill if none was passed.
    const baseStyle = (isSelf ? opts && opts.selfMentionStyle : opts && opts.mentionStyle)
      || DEFAULT_MENTION_STYLE;
    // The mentioned user's own username color (chosen or role default), looked
    // up by id so a mention reads in THAT user's color. A self-mention keeps
    // its inverted "you got pinged" fill but tints the background with the
    // user color; a regular mention tints the text color.
    const userColor = opts && opts.mentionColors && opts.mentionColors[String(userId)];
    let style = baseStyle;
    if (userColor) {
      style = isSelf
        ? { ...baseStyle, backgroundColor: userColor }
        : { ...baseStyle, color: userColor };
    }
    nodes.push(
      <span
        key={`${keyPrefix}-mention${i}`}
        className={isSelf ? 'mention mention-self' : 'mention'}
        data-userid={userId}
        style={style}
      >
        @{name}
      </span>
    );

    lastIndex = match.index + match[0].length;
    i += 1;
  }

  const tail = text.slice(lastIndex);
  if (tail) nodes.push(...splitLinks(tail, `${keyPrefix}-mend`));
  return nodes;
}

// Fallback mention pill styling if the caller passes no theme-derived style.
const DEFAULT_MENTION_STYLE = {
  color: '#00d9ff',
  backgroundColor: 'rgba(0, 217, 255, 0.14)',
  borderRadius: 4,
  padding: '0 3px',
  fontWeight: 600,
};

/**
 * Split a string into text segments and <a> link nodes. Links are extracted
 * first so inline markers inside a URL are never interpreted as formatting.
 * Non-link segments are then handed to renderInline.
 */
function splitLinks(text, keyPrefix) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let i = 0;
  // Reset stateful global regex before iterating.
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const plain = text.slice(lastIndex, match.index);
    if (plain) nodes.push(...renderInline(plain, `${keyPrefix}-t${i}`));

    let url = match[0];
    // Trim trailing punctuation back into the surrounding text.
    const trailing = (url.match(TRAILING_PUNCT) || [''])[0];
    if (trailing) url = url.slice(0, url.length - trailing.length);

    nodes.push(
      <a
        key={`${keyPrefix}-a${i}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {url}
      </a>
    );
    if (trailing) nodes.push(trailing);

    lastIndex = match.index + match[0].length;
    i += 1;
  }

  const tail = text.slice(lastIndex);
  if (tail) nodes.push(...renderInline(tail, `${keyPrefix}-tend`));
  return nodes;
}

/**
 * Resolve a user's effective username color: their chosen color, else the role
 * default. Shared by the message list (mention pills + author names) and the
 * composer's mention pill so a mention always renders in the SAME color as the
 * mentioned user's name. Mirrors the server's role-default fallback.
 *
 * @param {?string} usernameColor  the user's chosen color (may be null)
 * @param {?string} role           the user's role (e.g. 'moderator')
 * @returns {string} a concrete hex color
 */
export function resolveUsernameColor(usernameColor, role) {
  return usernameColor || (role === 'moderator' ? '#ff9900' : '#999999');
}

/**
 * Turn a plain message string into an array of safe React nodes with mentions,
 * bold, italic, underline, and auto-linked URLs applied.
 *
 * @param {string} text
 * @param {{ currentUserId?: string }} [opts] - currentUserId highlights
 *        self-mentions with the stronger "mention-self" variant.
 * @returns {React.ReactNode[]}
 */
export function renderRichText(text, opts) {
  if (!text) return [];
  return splitMentions(decodeBasicEntities(String(text)), 'rt', opts);
}

/**
 * True if `content` contains a mention of `userId` (i.e. an `@[name](userId)`
 * token whose id matches). Used to decide whether an incoming message should
 * ding the recipient. Decodes entities first so it matches the same shape the
 * renderer sees.
 *
 * @param {string} content
 * @param {string} userId
 * @returns {boolean}
 */
export function containsMentionOf(content, userId) {
  if (!content || userId == null) return false;
  const target = String(userId);
  const decoded = decodeBasicEntities(String(content));
  let match;
  MENTION_REGEX.lastIndex = 0;
  while ((match = MENTION_REGEX.exec(decoded)) !== null) {
    if (String(match[2]) === target) return true;
  }
  return false;
}

/**
 * Decode the basic HTML entities the server-side sanitizer leaves behind.
 *
 * The backend sanitizer (messageService.sanitizeContent) strips all HTML tags
 * and, as a side effect of DOMPurify, entity-encodes bare `< > " &` — it stores
 * that encoded form deliberately, because it is the safe shape for any consumer
 * that renders content as HTML. THIS client renders content as React text nodes
 * (never dangerouslySetInnerHTML), which do not decode entities, so we decode
 * here at render time — `5 &lt; 10` shows as `5 < 10`. This is XSS-safe: the
 * decoded string only ever becomes text-node children, never markup.
 * Decode &amp; LAST so `&amp;lt;` round-trips to the literal `&lt;`, not `<`.
 */
function decodeBasicEntities(text) {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Wire content → editable plain text: decode the basic entities and flatten
 * `@[name](id)` mention markup to a literal `@name`. Used by "Save as quick
 * reply" (snippets are plain text; a re-sent snippet just mentions by name).
 *
 * @param {string} content
 * @returns {string}
 */
export function wireToPlainText(content) {
  if (!content) return '';
  MENTION_REGEX.lastIndex = 0;
  return decodeBasicEntities(String(content)).replace(MENTION_REGEX, '@$1');
}

// NOTE: The textarea-era composition helpers (wrapSelection, wrapColor,
// insertAtCursor, findActiveMention, insertMention) were removed when the
// composer migrated to a Lexical contenteditable editor. Those concerns now
// live in chat/src/components/composer/ (the editor nodes/plugins build the
// node tree directly and serialize.js renders it back to this wire format).
// The DISPLAY + presence functions above (renderRichText, containsMentionOf,
// decodeBasicEntities) are unchanged — they remain the single source of truth
// for the wire format this serializer must reproduce.
